import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock, Truck, MapPin, Phone, MessageCircle, Navigation, Store, Flame, Gift } from 'lucide-react';
import api from '@/utils/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { formatGs, productImg, resolveUploadUrl } from '@/utils/helpers';
import PushBanner from '@/components/PushBanner';
import IOSInstallBanner from '@/components/IOSInstallBanner';

export default function HomePage({ onAddressClick }) {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [hero, setHero] = useState({});
  const [branches, setBranches] = useState([]);
  const [branchStatuses, setBranchStatuses] = useState({});
  const [todayPromos, setTodayPromos] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const { zone, branchId } = useCart();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    api.get('/categorias?active=true').then(r => setCategories(r.data.categorias)).catch(() => {});
    api.get('/settings/home').then(r => setHero(r.data)).catch(() => {});
    const url = branchId ? `/productos?branch_id=${branchId}&active=true` : '/productos?active=true';
    api.get(url).then(r => {
      const prods = r.data.productos.filter(p => p.image_url);
      setFeatured(prods.slice(0, 4));
    }).catch(() => {});
    // Branches + statuses
    api.get('/sucursales?active=true').then(r => setBranches(r.data.sucursales || [])).catch(() => {});
    api.get('/sucursales/status-all').then(r => {
      const map = {};
      (r.data.statuses || []).forEach(s => { map[s.branch_id] = s; });
      setBranchStatuses(map);
    }).catch(() => {});
    // Today's promos
    const promoUrl = branchId ? `/promociones/hoy?branch_id=${branchId}` : '/promociones/hoy';
    api.get(promoUrl).then(r => setTodayPromos(r.data.promociones || [])).catch(() => {});
  }, [branchId]);

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/loyalty/status').then(r => setLoyalty(r.data)).catch(() => {});
    } else { setLoyalty(null); }
  }, [isAuthenticated]);

  const heroImage = hero.home_hero_image || 'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?auto=format&fit=crop&w=800&q=60';
  const heroSrc = resolveUploadUrl(heroImage);
  const heroTitle = hero.home_title || 'Sushi *premium* a tu puerta';
  const heroSubtitle = hero.home_subtitle || 'Pedí en Asunción con entrega rápida';
  const heroSecondary = hero.home_secondary_text || '';
  const showOverlay = hero.home_overlay !== 'false';

  return (
    <div data-testid="home-page" className="pb-nav">
      {/* Hero */}
      <section className="relative h-64 md:h-80 overflow-hidden">
        {showOverlay && <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/40 via-[#0a0a0a]/60 to-[#0a0a0a]" />}
        <img src={heroSrc} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4" style={{ zIndex: 1 }}>
          <img
            src="/logo-transparent.png"
            alt="Sushi Express"
            data-testid="hero-logo"
            className="h-20 md:h-32 mx-auto mb-6 animate-fade-in drop-shadow-[0_0_24px_rgba(232,76,28,0.5)]"
          />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight" dangerouslySetInnerHTML={{ __html: heroTitle.replace(/\*(.*?)\*/g, '<span class="text-[#E84C1C]">$1</span>').replace(/\n/g, '<br/>') }} />
          <p className="text-zinc-400 text-sm mt-2">{heroSubtitle}</p>
          {heroSecondary && <p className="text-zinc-500 text-xs mt-1">{heroSecondary}</p>}
        </div>
      </section>

      {/* Zone indicator */}
      {!zone && (
        <button data-testid="home-set-address" onClick={onAddressClick} className="mx-4 mt-4 w-[calc(100%-2rem)] flex items-center gap-3 p-4 rounded-xl bg-zinc-900 border border-zinc-800/50 text-left hover:border-[#E84C1C]/30 transition-colors">
          <MapPin size={20} className="text-[#E84C1C] shrink-0" />
          <div>
            <p className="text-sm font-medium">Elegí tu ubicación</p>
            <p className="text-xs text-zinc-400">Para calcular envío y hacer pedidos</p>
          </div>
          <ChevronRight size={18} className="text-zinc-500 ml-auto" />
        </button>
      )}

      {zone && (
        <div className="mx-4 mt-4 flex gap-3">
          <div className="flex-1 flex items-center gap-2 p-3 rounded-xl bg-zinc-900 border border-zinc-800/50">
            <Truck size={16} className="text-[#E84C1C]" />
            <div>
              <p className="text-xs text-zinc-400">Envío</p>
              <p className="text-sm font-medium">{formatGs(zone.delivery_price)}</p>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-2 p-3 rounded-xl bg-zinc-900 border border-zinc-800/50">
            <Clock size={16} className="text-[#E84C1C]" />
            <div>
              <p className="text-xs text-zinc-400">Tiempo est.</p>
              <p className="text-sm font-medium">{zone.estimated_delivery_time_minutes} min</p>
            </div>
          </div>
        </div>
      )}

      {/* Push notifications banner */}
      <PushBanner />
      <IOSInstallBanner />

      {/* Today's promos */}
      {todayPromos.length > 0 && (
        <section data-testid="today-promos" className="mt-6 px-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-orange-500/15 flex items-center justify-center">
              <Flame size={16} className="text-orange-400" />
            </div>
            <h3 className="text-lg font-semibold">Promos de hoy</h3>
          </div>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
            {todayPromos.map(promo => (
              <Link
                key={promo.id}
                to={`/producto/${promo.product_id}`}
                data-testid={`promo-card-${promo.id}`}
                className="shrink-0 w-52 bg-gradient-to-br from-orange-500/10 to-zinc-900 border border-orange-500/20 rounded-xl overflow-hidden hover:border-orange-500/40 transition-colors group"
              >
                {promo.image_url && (
                  <div className="aspect-[16/9] overflow-hidden bg-zinc-800">
                    <img src={productImg(promo.image_url)} alt={promo.product_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                )}
                <div className="p-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400">{promo.label}</span>
                  <p className="text-sm font-medium mt-1.5 truncate">{promo.product_name}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-base font-bold text-orange-400">{formatGs(promo.promo_price)}</span>
                    {promo.regular_price && <span className="text-xs text-zinc-500 line-through">{formatGs(promo.regular_price)}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Loyalty progress */}
      {loyalty?.active && (
        <section data-testid="loyalty-progress" className="mt-5 px-4">
          <div className={`rounded-xl p-4 border ${loyalty.reward_available ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-zinc-900/70 border-zinc-800/50'}`}>
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${loyalty.reward_available ? 'bg-emerald-500/20' : 'bg-amber-500/15'}`}>
                <Gift size={16} className={loyalty.reward_available ? 'text-emerald-400' : 'text-amber-400'} />
              </div>
              <div>
                {loyalty.reward_available ? (
                  <>
                    <p className="text-sm font-semibold text-emerald-400">Tu {loyalty.reward_value}% OFF está disponible</p>
                    <p className="text-[11px] text-emerald-400/70">Te quedan {loyalty.remaining_uses} pedido{loyalty.remaining_uses !== 1 ? 's' : ''} con descuento</p>
                  </>
                ) : (
                  <p className="text-sm font-semibold">Estás a {loyalty.remaining_orders} pedido{loyalty.remaining_orders !== 1 ? 's' : ''} de tu descuento</p>
                )}
              </div>
            </div>
            {!loyalty.reward_available && (
              <>
                <div className="w-full h-2 rounded-full bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-500 to-[#E84C1C] transition-all duration-700"
                    style={{ width: `${Math.min(100, (loyalty.total_orders / loyalty.required_orders) * 100)}%` }}
                  />
                </div>
                <p className="text-[11px] text-zinc-500 mt-1.5">{loyalty.total_orders} / {loyalty.required_orders} pedidos este mes · Válido por {loyalty.reward_valid_orders || 2} pedidos</p>
              </>
            )}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="mt-8 px-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Categorías</h3>
          <Link to="/menu" className="text-xs text-[#E84C1C] flex items-center gap-1">Ver menú <ChevronRight size={14} /></Link>
        </div>
        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
          {categories.map(cat => (
            <Link
              key={cat.id}
              to={`/menu?category=${cat.id}`}
              data-testid={`home-cat-${cat.id}`}
              className="shrink-0 px-5 py-3 rounded-xl bg-zinc-900 border border-zinc-800/50 hover:border-[#E84C1C]/40 transition-colors text-sm font-medium whitespace-nowrap"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="mt-8 px-4">
          <h3 className="text-lg font-semibold mb-4">Destacados</h3>
          <div className="grid grid-cols-2 gap-3">
            {featured.map(p => (
              <Link
                key={p.id}
                to={`/producto/${p.id}`}
                data-testid={`home-product-${p.id}`}
                className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden hover:border-[#E84C1C]/40 transition-colors group"
              >
                <div className="aspect-[4/3] overflow-hidden bg-zinc-800">
                  <img src={productImg(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-[#E84C1C] text-sm font-bold mt-1">
                    {p.branch_price ? formatGs(p.branch_price) : ''}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
      {/* Branches section */}
      {branches.length > 0 && (
        <section data-testid="branches-section" className="mt-10 px-4 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <Store size={20} className="text-[#E84C1C]" />
            <h2 className="text-lg font-bold">Nuestras Sucursales</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {branches.map(b => {
              const st = branchStatuses[b.id];
              const isOpen = st?.is_open_now;
              const isPaused = st?.pause_until;
              const isClosedToday = st?.is_closed_today;
              let badgeColor = 'bg-red-500/15 text-red-400';
              let badgeText = 'Cerrado';
              if (isClosedToday) { badgeText = 'Cerrado hoy'; }
              else if (isPaused) { badgeColor = 'bg-amber-500/15 text-amber-400'; badgeText = 'Pausado'; }
              else if (isOpen) { badgeColor = 'bg-emerald-500/15 text-emerald-400'; badgeText = 'Abierto ahora'; }

              return (
                <div key={b.id} data-testid={`branch-card-${b.id}`} className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 hover:border-zinc-700 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-sm">{b.name}</h3>
                    <span data-testid={`branch-status-${b.id}`} className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${badgeColor}`}>
                      {badgeText}
                    </span>
                  </div>
                  {st?.message && (
                    <p className="text-xs text-zinc-500 mb-3">{st.message}</p>
                  )}
                  <div className="space-y-1.5 text-xs text-zinc-400">
                    {b.address && (
                      <p className="flex items-center gap-1.5"><MapPin size={12} className="text-zinc-500 shrink-0" /> {b.address}</p>
                    )}
                    {b.city && (
                      <p className="flex items-center gap-1.5"><Navigation size={12} className="text-zinc-500 shrink-0" /> {b.city}</p>
                    )}
                    {b.phone && (
                      <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                        <Phone size={12} className="text-zinc-500 shrink-0" /> {b.phone}
                      </a>
                    )}
                  </div>
                  <div className="flex gap-2 mt-4">
                    {b.whatsapp_number && (
                      <a
                        href={`https://wa.me/${b.whatsapp_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`branch-whatsapp-${b.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium transition-colors"
                      >
                        <MessageCircle size={14} /> WhatsApp
                      </a>
                    )}
                    {(b.lat && b.lng) && (
                      <a
                        href={`https://www.google.com/maps?q=${b.lat},${b.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`branch-maps-${b.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#E84C1C] hover:bg-[#d43f12] text-white text-xs font-medium transition-colors"
                      >
                        <MapPin size={14} /> Cómo llegar
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
